<template>
  <div>
    <table class="table table-sm table-hover bg-white" v-if="cashflow && cashflow.length">
      <caption class="table-caption text-left" v-if="isPortfolio && isMatrixClicked" style="padding-left:1rem;">
        {{ dealName }} CIRT Cash Flow{{ ', Scenario Name: ' + scenarioName }}
        <b-form-checkbox id="show_debug_info" v-model="showDebugInfo" class="transformed">Show Trigger Info</b-form-checkbox>
      </caption>
      <caption class="table-caption text-left" v-else-if="isPortfolio" style="padding-left:1rem;">
        CIRT Portfolio Cash Flow{{ ', Scenario Name: ' + scenarioName }}
        <b-form-checkbox id="show_debug_info" v-model="showDebugInfo" class="transformed">Show Trigger Info</b-form-checkbox>
      </caption>
      <caption class="table-caption text-left" v-else style="padding-left:1rem;">
        {{ dealName }} CIRT Cash Flow{{ isMatrixClicked ? ', CPR; ' + cpr + '%, CDR; ' + cdr + '%' : '' }}
        <b-form-checkbox id="show_debug_info" v-model="showDebugInfo" class="transformed">Show Trigger Info</b-form-checkbox>
      </caption>
      <thead>
      <tr class="font-weight-bold">
        <td class="sticky">Totals</td>
        <td class="sticky"></td>
        <td class="sticky"></td>
        <td class="text-right sticky">{{ totals.ReductionInLiability }}</td>
        <td class="text-right sticky">{{ totals.InsurerPaidLosses }}</td>
        <td class="text-right sticky">{{ totals.InsurancePremium }}</td>
        <td class="text-right sticky"></td>
        <td class="text-right sticky"></td>
        <td class="text-right sticky"></td>
        <td class="text-right sticky">{{ totals.NetInsuranceCashflow }}</td>
        <td class="text-right sticky"></td>
        <td class="text-right sticky">{{ totals.CollateralLosses }}</td>
        <td class="text-right sticky"></td>
        <td class="text-right sticky"></td>
        <td class="text-right sticky"></td>
        <td class="text-right sticky"></td>
        <template v-if="compDebugData">
          <template v-for="edf of compDebugData.rowsEmptyDataFixer">
            <td class="text-right sticky debug">{{edf}}</td>
          </template>
        </template>
      </tr>
      <tr>
        <!-- Add sticky-header class when totals are available -->
        <th class="sticky sticky-header">Period</th>
        <th class="text-right text-nowrap sticky sticky-header">Payment <br> Date</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Outstanding Limit' : 'Remaining Liability' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Reduction In Limit' : 'Reduction In Liability' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Limit Losses' : 'Insurer Paid Losses' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Premium' : 'Insurance Premium' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Required Collateral' : 'Collateral Trust Account' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Collateral Change' : 'Collateral Account Change' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Collateral Interest' : 'Interest' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Re-Insurance Cashflow' : 'Net Insurance Cashflow' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Ref Pool Balance' : 'Collateral Balance' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Ref Pool Losses' : 'Collateral Losses' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'CPR' : 'CPR Rate' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'CDR' : 'CDR Rate' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Severity' : 'Loss Severity Rate' }}</th>
        <th class="text-right sticky sticky-header">{{ (isPortfolio && isReinsurancePortfolio) || !isPortfolio ? 'Delinquency' : 'Delinquency Rate' }}</th>
        <template v-if="compDebugData">
          <template v-for="h of compDebugData.headers">
            <th class="text-right sticky sticky-header debug">{{h}}</th>
          </template>
        </template>
      </tr>
      </thead>
      <tbody>
      <template v-for="row of cashflow">
        <tr :key=row.Period v-bind:class="[{ dark: row.IsHistorical }]">
          <td>{{ row.Period }}</td>
          <td class="text-right">{{ row.PaymentDate }}</td>
          <td class="text-right">{{ row.RemainingLiability }}</td>
          <td class="text-right">{{ row.ReductionInLiability }}</td>
          <td class="text-right">{{ row.InsurerPaidLosses }}</td>
          <td class="text-right">{{ skipFirstRow(row.Period, row.InsurancePremium) }}</td>
          <td class="text-right">{{ row.CollateralTrustAccount }}</td>
          <td class="text-right">{{ row.CollateralAccountChange }}</td>
          <td class="text-right">{{ skipFirstRow(row.Period, row.CollateralAccountInterest) }}</td>
          <td class="text-right">{{ row.NetInsuranceCashflow }}</td>
          <td class="text-right">{{ row.CollateralBalance }}</td>
          <td class="text-right">{{ row.CollateralLosses }}</td>
          <td class="text-right">{{ skipFirstRow(row.Period, row.CprRate) }}</td>
          <td class="text-right">{{ skipFirstRow(row.Period, row.CdrRate) }}</td>
          <td class="text-right">{{ skipFirstRow(row.Period, row.LossSeverityRate) }}</td>
          <td class="text-right">{{ skipFirstRow(row.Period, row.DelinquencyRate) }}</td>
          <template v-if="compDebugData && (row.Period >= historicalCashflowItemsCount)">
            <template v-for="cell of compDebugData.rows[row.Period - historicalCashflowItemsCount]">
                <td class="debug">{{cell}}</td>
            </template>
          </template>
        </tr>
      </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import objectToTable         from "@/components/common/objectToTable";
import UtilsTableDataBuilder from "@/utilities/UtilsTableDataBuilder";

export default {
  props: {
    cashflow:               Array,
    dealName:               String,
    isPortfolio:            Boolean,
    isReinsurancePortfolio: Boolean,
    totals:                 Object,
    isMatrixClicked:        Boolean,
    cpr:                    Number,
    cdr:                    Number,
    scenarioName:           String,
    miscVariables:          Array
  },
  data() {
    return {
      showDebugInfo: false
    }
  },
  computed: {
    historicalCashflowItemsCount: function() {
      return (this.cashflow || []).reduce((sum, cashflow) => {
        return sum + (cashflow?.IsHistorical ? 1 : 0);
      }, 0);
    },
    compDebugData: function() {
      let res = null;
      if (this.showDebugInfo && this.miscVariables.length > 0) {
        const columnFormatters = [
          {DbName: "Pd", DataType: "String. Aligned left"},
          {DbName: "Period", DataType: "String. Aligned left"},
        ];
        res = new UtilsTableDataBuilder(this.miscVariables, columnFormatters);
      }
      return res;
    }
  },
  methods:    {
    skipFirstRow(index, value) {
      return index != 0 ? value : '';
    },
    toMatrix() {
      let totals = [
        'Totals',
        ,
        ,
        this.totals.ReductionInLiability,
        this.totals.InsurerPaidLosses,
        this.totals.InsurancePremium,
        ,
        ,
        ,
        this.totals.NetInsuranceCashflow,
        ,
        this.totals.CollateralLosses
      ];

      let cashflow = [
        [
          'Period',
          'Payment Date',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Outstanding Limit'
              : 'Remaining Liability',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Reduction In Limit'
              : 'Reduction In Liability',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Limit Losses'
              : 'Insurer Paid Losses',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Premium'
              : 'Insurance Premium',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Required Collateral'
              : 'Collateral Trust Account',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Collateral Change'
              : 'Collateral Account Change',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Collateral Interest'
              : 'Interest',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Re-Insurance Cashflow'
              : 'Net Insurance Cashflow',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Ref Pool Balance'
              : 'Collateral Balance',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Ref Pool Losses'
              : 'Collateral Losses',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'CPR'
              : 'CPR Rate',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'CDR'
              : 'CDR Rate',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Severity'
              : 'Loss Severity Rate',
          (this.isPortfolio && this.isReinsurancePortfolio) || !this.isPortfolio
              ? 'Delinquency'
              : 'Delinquency Rate'
        ]
      ];

      for (let i = 0; i < this.cashflow.length; i++) {
        cashflow.push([
          this.cashflow[i].Period,
          this.cashflow[i].PaymentDate,
          this.cashflow[i].RemainingLiability,
          this.cashflow[i].ReductionInLiability,
          this.cashflow[i].InsurerPaidLosses,
          this.skipFirstRow(this.cashflow[i].Period, this.cashflow[i].InsurancePremium),
          this.cashflow[i].CollateralTrustAccount,
          this.cashflow[i].CollateralAccountChange,
          this.skipFirstRow(this.cashflow[i].Period, this.cashflow[i].CollateralAccountInterest),
          this.cashflow[i].NetInsuranceCashflow,
          this.cashflow[i].CollateralBalance,
          this.cashflow[i].CollateralLosses,
          this.skipFirstRow(this.cashflow[i].Period, this.cashflow[i].CprRate),
          this.skipFirstRow(this.cashflow[i].Period, this.cashflow[i].CdrRate),
          this.skipFirstRow(this.cashflow[i].Period, this.cashflow[i].LossSeverityRate),
          this.skipFirstRow(this.cashflow[i].Period, this.cashflow[i].DelinquencyRate)
        ]);
      }
      let result = [totals];
      result     = result.concat(cashflow);

      return result;
    },
  },
  components: {
    objectToTable
  }
};
</script>

<style lang="scss">
.dark {
  background-color: #0080ff26;
}
</style>
